import { useCallback, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import type { Me } from '../lib/me'
import { can, canGallery } from '../lib/me'
import { canReachShopAdmin } from '../lib/shop'
import { supabase } from '../lib/supabase'
import { myLanes } from '../lib/support'
import { messageOf } from '../lib/errors'
import { useHubLang } from '../lib/i18n'
import { PHOTO_REVIEW } from '../lib/hubCopy'
import type { PersonOption } from '../lib/stars'
import {
  REVIEW_OUTCOMES,
  RESTRICTABLE_USES,
  decidePhotoReview,
  handPhotoReview,
  loadReviewQueue,
  type QueueItem,
  type RestrictableUse,
  type ReviewOutcome,
  type ReviewState,
} from '../lib/photoReview'
import { AppShell } from '../components/AppShell'
import { RoomThumb } from '../components/RoomThumb'
import { PhotosBar } from '../components/PhotosBar'
import { HelpButton } from '../components/HelpButton'
import { PersonField } from '../components/GiveRecognition'

/**
 * `#/photos/reviews` — 152. Photo review requests.
 *
 * Gallery admins (photo.location) see every request in their tenant; a person
 * a request was handed to (HLP-31's shape) sees that one, without who asked.
 * The server decides both and refuses anybody else (rule 12) — this page shows
 * the refusal rather than an empty list.
 *
 * ── WHAT IS NOT ON THIS PAGE, ON PURPOSE ────────────────────────────────────
 * No count of requests per poster or per requester, and no poster history:
 * the moderation queue's constraint (B09 §5). The page shows requests, each on
 * its own. A consent withdrawal is labelled as standing, and the decision form
 * says plainly that a review cannot overrule it — only a mistaken identity.
 */
export function PhotoReviews({ me }: { me: Me }) {
  const lang = useHubLang()
  const t = PHOTO_REVIEW[lang]
  const [params] = useSearchParams()
  const focus = params.get('open')
  const [state, setState] = useState<ReviewState>('open')
  const [items, setItems] = useState<QueueItem[] | null>(null)
  const [total, setTotal] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [refused, setRefused] = useState(false)


  const load = useCallback(async () => {
    setError(null)
    try {
      const page = await loadReviewQueue(state)
      setItems(page.items)
      setTotal(page.total)
      setRefused(false)
    } catch (e) {
      const code = (e as { code?: string } | null)?.code
      if (code === '42501') setRefused(true)
      else setError(messageOf(e) || t.couldNotSave)
      setItems([])
    }
  }, [state, t.couldNotSave])

  useEffect(() => {
    setItems(null)
    void load()
  }, [load])

  async function more() {
    if (!items) return
    try {
      const page = await loadReviewQueue(state, null, items.length)
      setItems([...items, ...page.items.filter((x) => !items.some((y) => y.id === x.id))])
      setTotal(page.total)
    } catch (e) {
      setError(messageOf(e))
    }
  }

  const dateOf = (iso: string) =>
    new Date(iso).toLocaleDateString(lang === 'es' ? 'es-CL' : 'en-CA', { year: 'numeric', month: 'short', day: 'numeric' })

  return (
    <AppShell
      current="Gallery"
      canAdmin={can(me, 'admin.roles')}
      canShop={canReachShopAdmin(me)}
      canModerate={can(me, 'content.moderate')}
      canStarsAdmin={can(me, 'stars.admin')}
      canPhotoRoom={canGallery(me)}
      canAnalytics={can(me, 'analytics.read')}
      canNotices={can(me, 'content.publish')}
      canAudit={can(me, 'audit.read')}
      supportLanes={myLanes(me)}
      userName={me.displayName}
      userInitials={initialsOf(me.displayName)}
      userRole={me.roles[0]?.name ?? 'No role'}
      locale={me.locale}
      onSignOut={() => supabase.auth.signOut()}
    >
      <div className="photoreviews">
        <PhotosBar section="gallery" canGallery={canGallery(me)}>
          <HelpButton title={t.queueTitle} label={t.queueHelpLabel}>
            {t.queueHelp.map((p) => (
              <p key={p}>{p}</p>
            ))}
          </HelpButton>
        </PhotosBar>
        <h1 className="photoreviews__title">{t.queueTitle}</h1>

        {refused ? (
          <p className="roles__error" role="alert">
            {t.refused}
          </p>
        ) : (
          <>
            <nav className="tabs" aria-label={t.queueTitle}>
              {(['open', 'closed'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  className={state === s ? 'tab tab--on' : 'tab'}
                  aria-pressed={state === s}
                  onClick={() => setState(s)}
                >
                  {s === 'open' ? t.tabOpen : t.tabClosed}
                </button>
              ))}
            </nav>
            {error && (
              <p className="roles__error" role="alert">
                {error}
              </p>
            )}
            {items === null && <p className="queue__empty">{t.loading}</p>}
            {items !== null && items.length === 0 && !error && (
              <p className="queue__empty">{state === 'open' ? t.empty : t.emptyClosed}</p>
            )}
            {items !== null && items.length > 0 && (
              <>
                <ul className="photoreviews__list">
                  {items.map((it) => (
                    <ReviewCard
                      key={it.id}
                      item={it}
                      focused={it.id === focus}
                      dateOf={dateOf}
                      onDone={() => void load()}
                    />
                  ))}
                </ul>
                {items.length < total && (
                  <>
                    <p className="photoroom__count">{t.shownOf(items.length, total)}</p>
                    <button type="button" className="btn btn--quiet btn--sm" onClick={() => void more()}>
                      {t.showMore}
                    </button>
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </AppShell>
  )
}

function ReviewCard({
  item,
  focused,
  dateOf,
  onDone,
}: {
  item: QueueItem
  focused: boolean
  dateOf: (iso: string) => string
  onDone: () => void
}) {
  const t = PHOTO_REVIEW[useHubLang()]
  const open = !item.decidedAt && !item.withdrawnAt
  const [handing, setHanding] = useState(false)
  const [pick, setPick] = useState<PersonOption | null>(null)
  const [handNote, setHandNote] = useState('')
  const [outcome, setOutcome] = useState<ReviewOutcome | null>(null)
  const [uses, setUses] = useState<RestrictableUse[]>([])
  const [identity, setIdentity] = useState(false)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function act(fn: () => Promise<void>) {
    setBusy(true)
    setError(null)
    try {
      await fn()
      onDone()
    } catch (e) {
      setError(messageOf(e) || t.couldNotSave)
    } finally {
      setBusy(false)
    }
  }

  const consent = item.reasons.includes('no_consent')
  const canDecide = outcome !== null && (outcome !== 'restrict' || uses.length > 0) && !busy

  return (
    <li className={focused ? 'photoreviews__item photoreviews__item--focus' : 'photoreviews__item'} id={`review-${item.id}`}>
      <div className="photoreviews__photo">
        <RoomThumb path={item.path} alt={item.caption ?? ''} className="photoreviews__img" />
      </div>
      <div className="photoreviews__body">
        <p className="photoreviews__reasons">
          {item.reasons.map((r) => (
            <span key={r} className={r === 'no_consent' ? 'photoreviews__reason photoreviews__reason--consent' : 'photoreviews__reason'}>
              {t.reasonShort[r]}
            </span>
          ))}
        </p>
        {item.caption && <p className="photoreviews__caption">{item.caption}</p>}
        {item.note && <blockquote className="photoreviews__note">{item.note}</blockquote>}
        <p className="photoreviews__meta">
          {[
            item.requesterName ? t.askedBy(item.requesterName) : null,
            t.askedOn(dateOf(item.requestedAt)),
            item.posterName ? t.postedBy(item.posterName) : null,
          ]
            .filter(Boolean)
            .join(' · ')}
          {item.contentId && (
            <>
              {' · '}
              <Link to={`/item/${item.contentId}?photo=${encodeURIComponent(item.path)}`}>{t.openPost}</Link>
            </>
          )}
        </p>
        {item.consentStanding && <p className="photoreviews__standing">{t.consentStanding}</p>}
        {item.moderationOpen && <p className="photoreviews__standing">{t.moderationOpen}</p>}
        {item.assignedToName && (
          <p className="photoreviews__meta">
            {item.access === 'assignee' ? t.handedToYou : t.handedTo(item.assignedToName)}
            {item.assignedNote ? ` — ${item.assignedNote}` : ''}
          </p>
        )}
        {item.decidedAt && item.outcome && (
          <p className="photoreviews__meta">
            {t.decidedBy(t.outcomePast[item.outcome], item.decidedByName ?? '', dateOf(item.decidedAt))}
            {item.outcome === 'restrict' && item.restrictedUses.length > 0
              ? ` (${item.restrictedUses.map((u) => t.useLabel[u].toLowerCase()).join(', ')})`
              : ''}
            {item.outcomeNote ? ` — ${item.outcomeNote}` : ''}
          </p>
        )}
        {item.withdrawnAt && <p className="photoreviews__meta">{t.withdrawnLine}</p>}

        {open && (
          <div className="photoreviews__actions">
            {item.access === 'admin' && !item.assignedTo && !handing && (
              <button type="button" className="btn btn--link" onClick={() => setHanding(true)}>
                {t.handButton}
              </button>
            )}
            {item.assignedTo && (
              <button
                type="button"
                className="btn btn--link"
                disabled={busy}
                onClick={() => void act(() => handPhotoReview(item.id, null, ''))}
              >
                {item.access === 'assignee' ? t.giveBack : t.takeBack}
              </button>
            )}
            {handing && (
              <div className="photoreviews__hand">
                <PersonField person={pick} onPick={setPick} label={t.handWho} />
                <label className="photoreviews__field">
                  <span className="composer__label">{t.handNote}</span>
                  <input value={handNote} maxLength={500} onChange={(e) => setHandNote(e.target.value)} />
                </label>
                <div className="photoreviews__row">
                  <button
                    type="button"
                    className="btn btn--primary btn--sm"
                    disabled={!pick || !handNote.trim() || busy}
                    onClick={() => pick && void act(() => handPhotoReview(item.id, pick.personId, handNote))}
                  >
                    {t.hand}
                  </button>
                  <button type="button" className="btn btn--quiet btn--sm" onClick={() => setHanding(false)}>
                    {t.cancel}
                  </button>
                </div>
              </div>
            )}

            <fieldset className="photoreviews__decide">
              <legend>{t.decideTitle}</legend>
              {REVIEW_OUTCOMES.map((o) => (
                <label key={o} className="photoreviews__opt">
                  <input type="radio" name={`outcome-${item.id}`} checked={outcome === o} onChange={() => setOutcome(o)} />
                  {t.outcome[o]}
                </label>
              ))}
              {outcome === 'restrict' && (
                <fieldset className="photoreviews__uses">
                  <legend>{t.restrictLegend}</legend>
                  {RESTRICTABLE_USES.map((u) => (
                    <label key={u} className="photoreviews__opt">
                      <input
                        type="checkbox"
                        checked={uses.includes(u)}
                        onChange={(e) => setUses((cur) => (e.target.checked ? [...cur, u] : cur.filter((x) => x !== u)))}
                      />
                      {t.useLabel[u]}
                    </label>
                  ))}
                </fieldset>
              )}
              {consent && (
                <>
                  <label className="photoreviews__opt">
                    <input type="checkbox" checked={identity} onChange={(e) => setIdentity(e.target.checked)} />
                    {t.identity}
                  </label>
                  <p className="photoreviews__hint">{t.identityNote}</p>
                </>
              )}
              <label className="photoreviews__field">
                <span className="composer__label">{t.outcomeNoteLabel}</span>
                <textarea rows={2} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
              </label>
              <button
                type="button"
                className="btn btn--primary btn--sm"
                disabled={!canDecide}
                onClick={() =>
                  outcome &&
                  void act(() =>
                    decidePhotoReview(item.id, { outcome, restrictedUses: uses, identityCorrected: consent && identity, note }),
                  )
                }
              >
                {busy ? t.saving : t.decide}
              </button>
            </fieldset>
          </div>
        )}
        {error && (
          <p className="roles__error" role="alert">
            {error}
          </p>
        )}
      </div>
    </li>
  )
}

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('')
}
